import {
	SampleMatrix,
	SampleSubMatrix,
	Severity,
	SPECIFICATION_ALLOWED_MIME_TYPES,
	SPECIFICATION_FILE_SIZE_LIMIT,
} from "node-library/dist/constants";
import { FrontendSampleSpecification } from "node-library/dist/portal";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../toast/showToastMessage";
import {
	SetStateAction,
	useEffect,
	useState,
	ChangeEvent,
	DragEvent,
} from "react";
import {
	generateUniqueFileName,
	getFilenameAndExtension,
} from "node-library/dist/helpers/file_helper";
import { RedCrossIcon, RedErrorIcon } from "../toast/errorIcons";
import { LinearProgress, Paper } from "@mui/material";
import {
	NeutralColor,
	PrimaryColor,
	SecondaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import { DeleteIcon, UploadIcon } from "../svgIcons";
import { GreenTickIcon } from "../toast/successIcons";
import { ModalWithButtons } from "./modal";
import { BlueButton } from "../button";
import { ButtonTextLoadingAnimation } from "../animation/ButtonLoadingAnimation";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { uploadAllToS3 } from "../../helpers/frontendFileOperationHelper";
import { CustomDropdown } from "../inputs";
import {
	getMatrixTranslationText,
	getSubMatrixKeysFromMatrix,
	getSubMatrixTranslationText,
} from "../../helpers/sampleHelper";
interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;

	matrix: SampleMatrix;
	subMatrix: SampleSubMatrix;
	folderSelectionDisabled: boolean;
	currentFolderSpecifications: Array<FrontendSampleSpecification>;
	setFolderSpecifications: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecification>>
	>;

	setSelectedSpecificationId: (id: string) => void;
}

export default function UploadSpecificationModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { open, setOpen, currentFolderSpecifications } = props;

	const [matrix, setMatrix] = useState<SampleMatrix>(props.matrix);
	const [subMatrix, setSubMatrix] = useState<SampleSubMatrix>(props.subMatrix);
	const [uploadStarted, setUploadStarted] = useState<boolean>(false);
	const [filesToUpload, setFilesToUpload] = useState<
		Array<{
			file: File;
			progress: number;
		}>
	>([]);
	const [dragging, setDragging] = useState<boolean>(false);

	useEffect(() => {
		if (open) {
			setFilesToUpload([]);
			setUploadStarted(false);
			setMatrix(props.matrix);
			setSubMatrix(props.subMatrix);
		}
	}, [open]);

	function setFileProgress(index: number, progress: number) {
		setFilesToUpload((prevFiles) =>
			prevFiles.map((fileObj, idx) =>
				idx === index ? { ...fileObj, progress: progress } : fileObj
			)
		);
	}
	function handleMatrixChange(matrix: SampleMatrix) {
		setMatrix(matrix);
		const newSubMatrix: SampleSubMatrix = getSubMatrixKeysFromMatrix(matrix)[0];
		setSubMatrix(newSubMatrix);
	}
	async function finalizeSubmit(
		uploadBatchId: string,

		errorFilename: Array<string>,
		errorSpecIds: Array<string>
	) {
		try {
			const response = await sendPOSTRequest(
				"/specifications/finalize-specification-upload",
				{
					uploadBatchId,
					errorSpecIds,
				}
			);

			const insertedSpecs: Array<FrontendSampleSpecification> =
				response.data.insertedSpecs;

			props.setFolderSpecifications((prevSpecs) =>
				prevSpecs.concat(insertedSpecs)
			);

			if (errorFilename.length === 0) {
				openSnackbar(
					translation("AllFilesUploadedSuccessfully"),
					Severity.SUCCESS,
					undefined,
					<GreenTickIcon />
				);
				if (insertedSpecs.length > 0) {
					props.setSelectedSpecificationId(insertedSpecs[0].specificationId);
				}
				setTimeout(() => {
					setOpen(false);
				}, 1000);
			} else {
				openSnackbar(
					translation("SomeFilesFailedToUpload", {
						count: errorFilename.length,
					}),
					Severity.ERROR,
					translation("SomeFilesFailedToUploadMsg", {
						files: errorFilename.join(", "),
					}),
					<RedErrorIcon />
				);
				setUploadStarted(false);
			}
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
			setUploadStarted(false);
		}
	}

	async function handleSubmit() {
		setUploadStarted(true);
		try {
			const response = await sendPOSTRequest(
				"/specifications/get-specification-presigned-upload-urls",
				{
					matrix: matrix,
					subMatrix: subMatrix,
					specFiles: filesToUpload.map((file) => {
						return {
							fullName: file.file.name,
							fileSize: file.file.size,
							type: file.file.type,
						};
					}),
				}
			);

			const uploadBatchId: string = response.data.uploadBatchId;

			const uploadUrls: Array<{
				fileName: string;
				specificationId: string;
				presignedPost: AWS.S3.PresignedPost;
			}> = response.data.uploadUrls;
			const results = await uploadAllToS3(
				filesToUpload,
				uploadUrls,
				setFileProgress
			);

			//check if all uploads are successful
			const errorFilename: Array<string> = [];
			const errorSpecIds: Array<string> = [];

			filesToUpload.forEach((file, index) => {
				if (results[index] == false) {
					let filename: string = file.file.name;
					const specId = uploadUrls.find((item) => {
						return item.fileName == filename;
					})?.specificationId as string;
					if (filename.length > 30) {
						filename = filename.substring(0, 30) + "...";
					}
					errorFilename.push(filename);
					errorSpecIds.push(specId);
				}
			});

			await finalizeSubmit(uploadBatchId, errorFilename, errorSpecIds);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		}
	}

	// Handle drag enter
	const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(true);
	};

	// Handle drag leave
	const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(false);
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(false);
		const droppedFiles = event.dataTransfer.files;
		if (droppedFiles && droppedFiles.length > 0) {
			validateAndAddFile(droppedFiles);
		}
	};

	// Handle file input change
	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = event.target.files;
		if (selectedFiles && selectedFiles.length > 0) {
			validateAndAddFile(selectedFiles);
		}
	};

	function validateAndAddFile(files: FileList) {
		const validFiles: Array<{
			file: File;
			progress: number;
		}> = [];
		const invalidFilename: Array<string> = [];
		let existingFiles: Array<{ fileName: string; extension: string }> =
			filesToUpload.map((file) => {
				return getFilenameAndExtension(file.file.name);
			});
		existingFiles = existingFiles.concat(
			currentFolderSpecifications.map((spec) => {
				return { fileName: spec.fileName, extension: spec.format };
			})
		);

		Array.from(files).forEach((file) => {
			const { fileName, extension } = getFilenameAndExtension(file.name);

			if (
				file.size <= SPECIFICATION_FILE_SIZE_LIMIT &&
				SPECIFICATION_ALLOWED_MIME_TYPES.includes(file.type)
			) {
				const uniqueName = generateUniqueFileName(
					fileName,
					extension,
					existingFiles
				);
				const newFile = new File([file], uniqueName, { type: file.type });

				validFiles.push({
					file: newFile,
					progress: 0,
				});
				const { fileName: uniqueFileName, extension: ignored } =
					getFilenameAndExtension(uniqueName);

				existingFiles.push({ fileName: uniqueFileName, extension });
			} else {
				let filename: string = file.name;
				if (filename.length > 30) {
					filename = filename.substring(0, 30) + "...";
				}
				invalidFilename.push(filename);
			}
		});

		if (invalidFilename.length > 0) {
			openSnackbar(
				translation("InvalidReportFile"),
				Severity.ERROR,
				translation("InvalidReportFileMsg", {
					files: invalidFilename.join(", "),
				}),
				<RedErrorIcon />
			);
		}
		setFilesToUpload((prevFiles) => [...prevFiles, ...validFiles]);
	}
	const handleRemoveFile = (fileName: string) => {
		setFilesToUpload((prevFiles) =>
			prevFiles.filter((file) => file.file.name !== fileName)
		);
	};

	const uploader: React.ReactNode = (
		<div>
			<Paper
				sx={{
					padding: 3,
					border: dragging
						? `2px dashed ${PrimaryColor.PRIMARY_800}`
						: `2px dashed ${NeutralColor.NEUTRAL_GREY_300}`,
					textAlign: "center",
					backgroundColor: "#F3F3F5",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					boxShadow: "none", // Add this line to remove the shadow
				}}
				onDragEnter={handleDragEnter}
				onDragOver={(e) => e.preventDefault()}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<div className="test-report-upload-icon-container">
					<UploadIcon></UploadIcon>
				</div>

				<input
					type="file"
					multiple
					onChange={handleFileChange}
					style={{ display: "none" }}
					id="file-input"
					accept={SPECIFICATION_ALLOWED_MIME_TYPES.join(",")}
				/>
				<div style={{ marginTop: "10px" }}>
					{translation("DragDropFileDescription")}
					<label
						htmlFor="file-input"
						style={{ cursor: "pointer", marginTop: 10 }}
					>
						<div>
							<b>
								<u>{translation("chooseFile")}</u>
							</b>
						</div>
					</label>
				</div>
			</Paper>

			<div className="upload-specification-folder-row">
				<CustomDropdown
					id="matrix-folder-dropdown"
					label={translation("MatrixFolder")}
					options={Object.values(SampleMatrix).map((value) => {
						return {
							value: value,
							title: getMatrixTranslationText(value, translation),
						};
					})}
					value={matrix}
					disabled={props.folderSelectionDisabled}
					setValue={handleMatrixChange}
				></CustomDropdown>
				<CustomDropdown
					id="sub-matrix-dropdown"
					label={translation("SubMatrix")}
					options={getSubMatrixKeysFromMatrix(matrix).map((value) => {
						return {
							value: value,
							title: getSubMatrixTranslationText(value, translation),
						};
					})}
					value={subMatrix}
					disabled={props.folderSelectionDisabled}
					setValue={(value) => {
						setSubMatrix(value);
					}}
				></CustomDropdown>
			</div>

			{/* Display the uploaded files */}
			<div style={{ marginTop: 3, maxHeight: "500px", overflow: "auto" }}>
				{filesToUpload.map((file, index) => {
					let rowIcon: React.ReactNode;
					if (file.progress == -1) {
						rowIcon = <RedCrossIcon></RedCrossIcon>;
					} else if (file.progress == 100) {
						rowIcon = (
							<GreenTickIcon
								size={20}
								strokeColor="white"
								fillColor={SemanticColor.SUCCESS}
							></GreenTickIcon>
						);
					} else if (!uploadStarted) {
						rowIcon = (
							<div
								style={{ cursor: "pointer" }}
								onClick={() => {
									handleRemoveFile(file.file.name);
								}}
							>
								<DeleteIcon></DeleteIcon>
							</div>
						);
					} else {
						rowIcon = <div></div>;
					}

					return (
						<>
							<div className="upload-report-file-row" key={index}>
								<div style={{ maxWidth: "80%", wordWrap: "break-word" }}>
									{file.file.name}
								</div>
								{rowIcon}
							</div>
							<LinearProgress
								sx={{
									borderRadius: "4px",
									backgroundColor: NeutralColor.NEUTRAL_GREY_100,
									"& .MuiLinearProgress-bar": {
										backgroundColor:
											file.progress === 100
												? SemanticColor.SUCCESS
												: SecondaryColor.SECONDARY_800,
									},
									"& .MuiLinearProgress-bar1Determinate": {
										animationDuration: "0.3s", // Adjust this value to speed up or slow down the animation
									},
									"& .MuiLinearProgress-determinate": {
										animationDuration: "0.3s", // Speed up the indeterminate animation by changing this duration
									},
								}}
								variant="determinate"
								value={file.progress}
							/>{" "}
						</>
					);
				})}
			</div>
		</div>
	);

	const updateStatusTimeModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={translation("Uploadfiles")}
			headerDescription={translation("UploadfilesDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						margin: "0px 20px",
					}}
				>
					<BlueButton
						key="update-order-status-time-button"
						id="update-order-status-time-button"
						size="flex"
						children={
							uploadStarted ? (
								<ButtonTextLoadingAnimation
									text={translation("Uploading")}
								></ButtonTextLoadingAnimation>
							) : (
								translation("Upload")
							)
						}
						disabled={uploadStarted || filesToUpload.length == 0}
						onClick={() => {
							handleSubmit();
						}}
					></BlueButton>
				</div>,
			]}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "10px",
					margin: "0px 20px",
				}}
			>
				{uploader}
			</div>
		</ModalWithButtons>
	);
	return updateStatusTimeModal;
}
